<script lang="ts" setup>
import {
  tpgIconFilePresent,
  tpgIconImage,
  tpgIconPlayCircleFilled,
  tpgIconSearch,
} from '@tpg/grammar-web-icons';
import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  PageChangeEvent,
  TpgChangeEvent,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarLocalLoader,
  GrammarModal,
  GrammarPaginator,
  GrammarTabBar,
  GrammarTabItem,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import { AttachmentMimeTypeFilter } from '@nora-monorepo/nora-api-client';
import {
  bgSecondary,
  flexCenter,
  textSecondary,
} from '@nora-monorepo/ui-shared';

import AttachmentRow from '#nora-chat-ui/components/modals/files-modal/AttachmentRow.vue';
import AttachmentSortMenu from '#nora-chat-ui/components/modals/files-modal/AttachmentSortMenu.vue';
import EditFileName from '#nora-chat-ui/components/modals/files-modal/EditFileName.vue';
import { useFilesModalStore } from '#nora-chat-ui/components/modals/files-modal/files-modal-store';
import type {
  FilterItem,
  SortOption,
} from '#nora-chat-ui/components/modals/files-modal/files-modal.types';

const SEARCH_DEBOUNCE = 350;

const store = useFilesModalStore();
const { t } = useI18n();

const { isOpen, attachments, total, isLoading, limit } = storeToRefs(store);
const searchInput = ref<string>();
const filter = ref<AttachmentMimeTypeFilter>();
const first = ref(0);
const selectedAttachment = ref<MessageAttachmentDTO | null>(null);
const selectedSort = ref<SortOption>();

const filterItems: FilterItem[] = [
  {
    label: t('attachments.attachmentFilterNames.allFiles'),
  },
  {
    label: t('attachments.attachmentFilterNames.images'),
    icon: tpgIconImage,
    filter: AttachmentMimeTypeFilter.IMAGE,
  },
  {
    label: t('attachments.attachmentFilterNames.videos'),
    icon: tpgIconPlayCircleFilled,
    filter: AttachmentMimeTypeFilter.VIDEO,
  },
  {
    label: t('attachments.attachmentFilterNames.documents'),
    icon: tpgIconFilePresent,
    filter: AttachmentMimeTypeFilter.APPLICATION,

  },

];

/**
 * Searches with the current filters, returning to the first page unless a specific page is requested.
 */
function runSearch(page?: PageChangeEvent) {
  const skip = page?.first ?? 0;

  first.value = skip;

  void store.search({
    query: searchInput.value ?? '',
    limit: page?.rows ?? limit.value,
    skip,
    sortBy: selectedSort.value?.sort.sortBy,
    sortOrder: selectedSort.value?.sort.sortOrder,
    filter: filter.value,
  });
}

/**
 * Debounced version of runSearch, to avoid excessive API calls when the user is typing in the search input.
 */
const searchAttachment = useDebounceFn(() => {
  runSearch();
}, SEARCH_DEBOUNCE);

/**
 * Opens the modal to edit the filename of the given attachment.
 * @param attachment
 */
function openEditFileNameModal(attachment: MessageAttachmentDTO) {
  selectedAttachment.value = attachment;
}

/**
 * Resets the modal state to its initial values.
 */
function reset() {
  searchInput.value = '';
  selectedSort.value = undefined;
  filter.value = undefined;
  first.value = 0;
  store.reset();
}

/**
 * Closes the modal and resets its state.
 */
function handleClose() {
  reset();

  isOpen.value = false;
}

/**
 * Determines if the given value is a valid AttachmentMimeTypeFilter.
 * @param value
 */
function isMimeTypeFilter(
  value: string | undefined,
): value is AttachmentMimeTypeFilter {
  return Object.values<string | undefined>(AttachmentMimeTypeFilter).includes(value);
}

/**
 * Handles the tab change event.
 * @param event
 */
function tabChange(event: TpgChangeEvent<string | undefined>) {
  const newFilter = event.value;
  filter.value = isMimeTypeFilter(newFilter) ? newFilter : undefined;

  runSearch();
}

/**
 * Selects the given sort value and runs a search with the new sorting.
 * @param value
 */
function selectSortValue(value: SortOption) {
  selectedSort.value = value;

  runSearch();
}

/**
 * Updates the filename of the selected attachment and resets the selected attachment.
 * @param newName
 */
function updateFileName(newName: string) {
  if (selectedAttachment.value) {
    store.updateAttachmentName(selectedAttachment.value.id, newName);
  }

  selectedAttachment.value = null;
}
</script>

<template>
  <GrammarModal
    :is-open="isOpen"
    :label="t('attachments.attachment')"
    :show-close-button="true"
    @close="handleClose"
  >
    <template #content>
      <GrammarTabBar
        :selected-value="filter"
        justify-content="stretch"
        @change="tabChange"
      >
        <GrammarTabItem
          v-for="filterItem of filterItems"
          :key="filterItem.filter ?? 'all'"
          :label="filterItem.label"
          :value="filterItem.filter"
          :leading-icon-template="filterItem.icon"
        />
      </GrammarTabBar>
      <div
        class="flex max-h-[calc(90vh-170px)] min-h-[calc(70vh-170px)] flex-col overflow-y-auto max-w-[95%] min-w-[max(304px,80vw)] lg:min-w-[60vw]"
      >
        <div class="flex flex-row justify-between p-3">
          <div class="flex w-full justify-between">
            <GrammarTextField
              v-model:value="searchInput"
              class="w-1/3"
              :leading-icon-template="tpgIconSearch"
              :placeholder="t('attachments.searchPlaceholder')"
              :form-associated="false"
              @input="searchAttachment"
            />

            <AttachmentSortMenu
              :selected-sort="selectedSort"
              @select="selectSortValue"
            />
          </div>
        </div>
        <div class="h-full max-h-full min-h-[48px]">
          <div v-if="isLoading" class="w-full h-full min-h-[48px]" :class="flexCenter">
            <GrammarLocalLoader />
          </div>

          <div
            v-else-if="total < 1"
            class="w-full h-full min-h-[48px]"
            :class="flexCenter"
          >
            <h1
              class="p-dialog-header text-md font-semibold leading-6 mb-3"
              :class="[textSecondary, bgSecondary]"
            >
              {{ t('attachments.noAttachments') }}
            </h1>
          </div>
          <div
            v-else
            class="max-h-full"
          >
            <AttachmentRow
              v-for="attachment in attachments"
              :key="attachment.id"
              :attachment="attachment"
              @edit="() => openEditFileNameModal(attachment)"
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <GrammarPaginator
        :first="first"
        :limit="limit"
        :total="total"
        :class="{ invisible: isLoading }"
        @page-change="runSearch"
      />
    </template>
  </GrammarModal>
  <EditFileName
    :attachment="selectedAttachment"
    @close="() => (selectedAttachment = null)"
    @update="updateFileName"
  />
</template>
